import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { useTranslations } from '@/lib/next-shim'

interface Option {
  value: string
  label: string
}

interface SelectInputProps<T extends FieldValues> {
  id: Path<T>
  label: string
  colSpan: string
  options: Option[]
  name?: Path<T>
  control: Control<T>
  required?: boolean
}

export default function SelectInput<T extends FieldValues>({ id, label, colSpan, options, name, control, required = false }: SelectInputProps<T>) {
  const t = useTranslations()
  return (
    <Controller
      name={name ? name : id}
      control={control}
      render={({ field, fieldState }) => (
        <div className={colSpan}>
          <label htmlFor={id as string} className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            id={id as string}
            {...field}
            aria-invalid={fieldState.error ? true : undefined}
            aria-describedby={fieldState.error ? `${id as string}-error` : undefined}
            className={`bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 h-[40px] pl-2 ${fieldState.error ? 'border-red-500' : 'border-gray-300'}`}
          >
            <option value="">{t('common.select')}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {fieldState.error && (
            <p id={`${id as string}-error`} className="mt-1 text-sm text-red-500" role="alert">{fieldState.error.message}</p>
          )}
        </div>
      )}
    />
  )
}
